import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import { deleteMasterCoupon, fetchMasterCoupons, saveMasterCoupon } from './adminApi';
import { normalizeCouponCode } from '../Manager/SubscriptionManagement/subscriptionApi';

const EMPTY_COUPON = {
  code: '',
  description: '',
  discount_percent: '',
  min_months: 1,
  discount_months: 12,
  max_uses: 1,
  valid_from: '',
  valid_until: '',
  status: 1,
};

function formatValidity(coupon) {
  const from = coupon.valid_from || 'now';
  const until = coupon.valid_until || 'no end';
  return `${from} → ${until}`;
}

const MasterCouponSettings = () => {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(EMPTY_COUPON);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCoupons();
  }, []);

  function loadCoupons() {
    setLoading(true);
    fetchMasterCoupons()
      .then((res) => {
        if (res.status) setCoupons(res.coupons || []);
      })
      .finally(() => setLoading(false));
  }

  function resetForm() {
    setEditingId(null);
    setForm(EMPTY_COUPON);
  }

  function editCoupon(coupon) {
    setEditingId(coupon.id);
    setForm({
      code: coupon.code || '',
      description: coupon.description || '',
      discount_percent: coupon.discount_percent ?? '',
      min_months: coupon.min_months || 1,
      discount_months: coupon.discount_months || coupon.min_months || 12,
      max_uses: coupon.max_uses || 1,
      valid_from: coupon.valid_from || '',
      valid_until: coupon.valid_until || '',
      status: coupon.status ?? 1,
    });
  }

  function handleChange(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const code = normalizeCouponCode(form.code);
    const percent = Number(form.discount_percent);
    const maxUses = parseInt(form.max_uses, 10);
    const minMonths = parseInt(form.min_months, 10) || 1;
    const discountMonths = parseInt(form.discount_months, 10) || 12;
    if (!code) {
      swal('Coupon', 'Enter a coupon code.', 'warning');
      return;
    }
    if (!Number.isFinite(percent) || percent <= 0 || percent > 100) {
      swal('Coupon', 'Discount percent must be between 0.01 and 100.', 'warning');
      return;
    }
    if (!Number.isFinite(maxUses) || maxUses < 1) {
      swal('Coupon', 'Max uses must be at least 1.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const result = await saveMasterCoupon({
        code,
        description: form.description,
        discount_percent: percent,
        min_months: minMonths,
        discount_months: discountMonths,
        max_uses: maxUses,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        status: Number(form.status),
      }, editingId);
      if (result.status) {
        swal('Saved', result.message || 'Coupon saved.', 'success');
        resetForm();
        loadCoupons();
      } else {
        swal('Coupon', result.message || 'Unable to save coupon.', 'warning');
      }
    } catch (e) {
      swal('Error', e.message || 'Unable to save coupon.', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function deactivate(coupon) {
    const confirmed = await swal({
      title: 'Deactivate coupon?',
      text: `${coupon.code} will no longer be accepted at checkout.`,
      icon: 'warning',
      buttons: ['Cancel', 'Deactivate'],
      dangerMode: true,
    });
    if (!confirmed) return;
    const result = await deleteMasterCoupon(coupon.id);
    if (result.status) {
      if (editingId === coupon.id) resetForm();
      loadCoupons();
    } else {
      swal('Coupon', result.message || 'Unable to deactivate coupon.', 'warning');
    }
  }

  return (
    <div className="row">
      <div className="col-lg-7">
        <div className="admin-panel">
          <h5>Promotion coupons</h5>
          <p className="text-muted small">
            Percentage off applies only to <strong>Discount months</strong> (example: 12 months @ 50% on a 24-month buy
            discounts the first 12 months only). <strong>Min months</strong> is just the shortest term that can unlock the code.
            A coupon replaces the plan advance-payment offer. Each code can be redeemed up to Max uses.
          </p>
          {loading ? (
            <p className="text-muted mb-0">Loading coupons...</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Code</th>
                    <th>Discount</th>
                    <th>Discount months</th>
                    <th>Min months</th>
                    <th>Uses</th>
                    <th>Validity</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-muted">No coupons yet. Create one on the right.</td>
                    </tr>
                  )}
                  {coupons.map((coupon) => (
                    <tr key={coupon.id}>
                      <td>
                        <strong>{coupon.code}</strong>
                        {coupon.description ? <div className="small text-muted">{coupon.description}</div> : null}
                      </td>
                      <td>{Number(coupon.discount_percent).toFixed(2)}%</td>
                      <td>{coupon.discount_months || coupon.min_months}</td>
                      <td>{coupon.min_months}</td>
                      <td>{coupon.used_count} / {coupon.max_uses}</td>
                      <td className="small">{formatValidity(coupon)}</td>
                      <td>{Number(coupon.status) === 1 ? 'Active' : 'Inactive'}</td>
                      <td className="text-nowrap">
                        <button type="button" className="btn btn-link btn-sm p-0 me-2" onClick={() => editCoupon(coupon)}>Edit</button>
                        {Number(coupon.status) === 1 && (
                          <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => deactivate(coupon)}>Deactivate</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <div className="col-lg-5">
        <form onSubmit={handleSubmit} className="admin-panel border rounded p-3">
          <h6>{editingId ? 'Edit coupon' : 'Create coupon'}</h6>
          <div className="mb-2">
            <label className="form-label">Discount code</label>
            <input
              className="form-control text-uppercase"
              value={form.code}
              onChange={(e) => handleChange('code', normalizeCouponCode(e.target.value))}
              onPaste={(e) => {
                e.preventDefault();
                const pasted = e.clipboardData?.getData('text') || '';
                handleChange('code', normalizeCouponCode(`${form.code || ''}${pasted}`));
              }}
              maxLength={32}
              placeholder="e.g. YEAR12"
              required
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="mb-2">
            <label className="form-label">Description</label>
            <input
              className="form-control"
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Internal note, optional"
            />
          </div>
          <div className="row g-2">
            <div className="col-6 mb-2">
              <label className="form-label">Discount %</label>
              <input
                type="number"
                min="0.01"
                max="100"
                step="0.01"
                className="form-control"
                value={form.discount_percent}
                onChange={(e) => handleChange('discount_percent', e.target.value)}
                required
              />
            </div>
            <div className="col-6 mb-2">
              <label className="form-label">Max uses</label>
              <input
                type="number"
                min="1"
                className="form-control"
                value={form.max_uses}
                onChange={(e) => handleChange('max_uses', e.target.value)}
                required
              />
            </div>
            <div className="col-6 mb-2">
              <label className="form-label">Discount months</label>
              <input
                type="number"
                min="1"
                max="36"
                className="form-control"
                value={form.discount_months}
                onChange={(e) => handleChange('discount_months', e.target.value)}
                required
              />
            </div>
            <div className="col-6 mb-2">
              <label className="form-label">Min months to unlock</label>
              <input
                type="number"
                min="1"
                max="36"
                className="form-control"
                value={form.min_months}
                onChange={(e) => handleChange('min_months', e.target.value)}
              />
            </div>
            <div className="col-6 mb-2">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
            <div className="col-6 mb-2">
              <label className="form-label">Valid from</label>
              <input
                type="date"
                className="form-control"
                value={form.valid_from}
                onChange={(e) => handleChange('valid_from', e.target.value)}
              />
            </div>
            <div className="col-6 mb-2">
              <label className="form-label">Valid until</label>
              <input
                type="date"
                className="form-control"
                value={form.valid_until}
                onChange={(e) => handleChange('valid_until', e.target.value)}
              />
            </div>
          </div>
          <p className="small text-muted mb-3">
            Example: Discount months = 12, Discount % = 50, Min months = 1 → a 24-month purchase gets 50% off the first 12 months only (₹18,000 off a ₹3,000/mo plan), not 50% of the full 24 months.
          </p>
          <button type="submit" className="btn btn-primary me-2" disabled={saving}>
            {editingId ? 'Update coupon' : 'Create coupon'}
          </button>
          {editingId && (
            <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>Cancel</button>
          )}
        </form>
      </div>
    </div>
  );
};

export default MasterCouponSettings;
